import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/shared/data/data.service';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss'
})
export class UserProfileComponent implements OnInit {

  // User Profile details
  user_profile: any = {};
  profile: any = {};
  stats: any = {
    total_orders: 0,
    total_spent: 0.0,
    last_order: null
  };

  // Password Model
  password = { old: '', new: '' };
  confirmNewPassword: string = '';

  // Tab and Mode States
  activeTab: string = 'profile';
  editMode: boolean = false;

  // Validation States
  profileErrors = { name: '', email: '', mobile: '' };
  passwordErrors = { old: '', new: '', confirm: '' };

  // Spinner states
  isLoadingProfile: boolean = false;
  isSavingProfile: boolean = false;
  isChangingPassword: boolean = false;

  // Toast Notification state
  toast = {
    show: false,
    message: '',
    type: 'success' // 'success' | 'error' | 'warning'
  };

   isUploadingImage: boolean = false;

  constructor(private http: HttpClient , private data : DataService) {}

  ngOnInit(): void {
    this.loadProfile();
  }

  showToast(message: string, type: 'success' | 'error' | 'warning' = 'success') {
    this.toast = { show: true, message, type };
    // Auto hide after 4 seconds
    setTimeout(() => {
      this.toast.show = false;
    }, 4000);
  }

  loadProfile() {
    const customerId = localStorage.getItem('customer_id');
    if (!customerId) {
      this.showToast('Please log in to view your profile.', 'error');
      return;
    }

    this.isLoadingProfile = true;
    const payload = { customer_id: customerId };

    this.http.post(this.data.base_url+'get_profile/', payload)
      .subscribe({
        next: (res: any) => {
          this.user_profile = res.user || {};
          this.profile = { ...this.user_profile }; // Assign clone for editing
          this.stats = res.stats || { total_orders: 0, total_spent: 0.0, last_order: null };
          
          if (res.user) {
            localStorage.setItem('customer_name', res.user.name || '');
            localStorage.setItem('customer_email', res.user.email || '');
            localStorage.setItem('customer_mobile', res.user.mobile || '');
            localStorage.setItem('profile_image', res.user.profile_image || '');
          }
          this.isLoadingProfile = false;
        },
        error: (err) => {
          this.showToast('Failed to retrieve profile data.', 'error');
          this.isLoadingProfile = false;
        }
      });
  }

  toggleEditMode(mode: boolean) {
    this.editMode = mode;
    if (mode) {
      this.profile = { ...this.user_profile }; // Reset profile edits to current profile state
      this.profileErrors = { name: '', email: '', mobile: '' };
    }
  }

  validateProfile(): boolean {
    this.profileErrors = { name: '', email: '', mobile: '' };
    let isValid = true;

    if (!this.profile.name || !this.profile.name.trim()) {
      this.profileErrors.name = 'Full Name is required.';
      isValid = false;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!this.profile.email || !emailPattern.test(this.profile.email)) {
      this.profileErrors.email = 'Please enter a valid email address.';
      isValid = false;
    }

    const mobilePattern = /^[0-9+\-\s()]{8,15}$/;
    if (!this.profile.mobile || !mobilePattern.test(this.profile.mobile)) {
      this.profileErrors.mobile = 'Please enter a valid mobile number (8-15 digits).';
      isValid = false;
    }

    return isValid;
  }

  updateProfile() {
    if (!this.validateProfile()) {
      this.showToast('Please correct the validation errors in the form.', 'warning');
      return;
    }

    const customerId = localStorage.getItem('customer_id');
    const payload = {
      ...this.profile,
      customer_user_id: customerId
    };

    this.isSavingProfile = true;
    this.http.post<any>(this.data.base_url+'update_profile/', payload)
      .subscribe({
        next: (res) => {
          if (res && res.status === 1) {
            this.showToast(res.message || 'Profile updated successfully!', 'success');
            this.editMode = false;
            this.loadProfile();
          } else {
            this.showToast((res && res.message) || 'Failed to update profile.', 'error');
          }
          this.isSavingProfile = false;
        },
        error: (err) => {
          this.showToast('Network error: Failed to update profile.', 'error');
          this.isSavingProfile = false;
        }
      });
  }

  validatePassword(): boolean {
    this.passwordErrors = { old: '', new: '', confirm: '' };
    let isValid = true;

    if (!this.password.old) {
      this.passwordErrors.old = 'Current password is required.';
      isValid = false;
    }

    if (!this.password.new || this.password.new.length < 6) {
      this.passwordErrors.new = 'New password must be at least 6 characters.';
      isValid = false;
    }

    if (this.password.new === this.password.old) {
      this.passwordErrors.new = 'New password must be different from current password.';
      isValid = false;
    }

    if (this.password.new !== this.confirmNewPassword) {
      this.passwordErrors.confirm = 'Confirm password does not match new password.';
      isValid = false;
    }

    return isValid;
  }

  changePassword() {
    if (!this.validatePassword()) {
      this.showToast('Please fix the password errors.', 'warning');
      return;
    }

    const customerId = localStorage.getItem('customer_id');
    if (!customerId) {
      this.showToast('User not logged in. Please log in again.', 'error');
      return;
    }

    const payload = {
      customer_id: parseInt(customerId),
      old_password: this.password.old,
      new_password: this.password.new
    };

    this.isChangingPassword = true;
    this.http.post<any>(this.data.base_url+'change_user_password/', payload)
      .subscribe({
        next: (res) => {
          if (res && res.status === 1) {
            this.showToast(res.message || 'Password changed successfully!', 'success');
            this.password = { old: '', new: '' };
            this.confirmNewPassword = '';
          } else {
            this.showToast((res && res.message) || 'Failed to change password.', 'error');
          }
          this.isChangingPassword = false;
        },
        error: (err) => {
          const errorMsg = err.error?.message || 'Failed to change password. Old password may be incorrect.';
          this.showToast(errorMsg, 'error');
          this.isChangingPassword = false;
        }
      });
  }


   triggerImageUpload(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.showToast('Please upload a valid image file.', 'warning');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.showToast('File size must be under 2MB.', 'warning');
      return;
    }

    const customerId = localStorage.getItem('customer_id');
    if (!customerId) {
      this.showToast('Customer not logged in.', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('customer_id', customerId);
    formData.append('image', file);

    this.isUploadingImage = true;
    this.http.post<any>(this.data.base_url+'upload_profile_image/', formData)
      .subscribe({
        next: (res) => {
          if (res && res.status === 1) {
            this.showToast('Profile image updated successfully!', 'success');
            this.user_profile.profile_image = res.profile_image;
            this.profile.profile_image = res.profile_image;
            localStorage.setItem('profile_image', res.profile_image || '');
            
          } else {
            this.showToast(res.message || 'Failed to upload profile image.', 'error');
          }
          this.isUploadingImage = false;
        },
        error: (err) => {
          this.showToast('Network error: Failed to upload image.', 'error');
          this.isUploadingImage = false;
        }
      });
  }



}
